/* Save PO files into a user-selected local folder using the File System
   Access API (Chrome/Edge). The chosen folder handle is persisted in
   IndexedDB so it survives reloads; permission is re-checked each session.
   saveBlobToFolder  — explicit save, may show the folder picker.
   autoFilePDF       — silent save, never shows the picker; only writes when
                       a folder was already chosen and permission is granted.
   Returns: folder name on success, null if cancelled/unavailable,
   false if the browser can't show a picker (caller falls back to download). */

const DB_NAME = "dvepl-fs";
const STORE = "handles";
const KEY = "po-folder";
let dirHandle = null;

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

async function idbGet() {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE).objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function idbSet(handle) {
  try {
    const db = await openDb();
    db.transaction(STORE, "readwrite").objectStore(STORE).put(handle, KEY);
  } catch { /* persistence is best-effort */ }
}

async function ensurePermission(handle, mayPrompt) {
  try {
    let perm = await handle.queryPermission({ mode: "readwrite" });
    if (perm === "granted") return true;
    if (!mayPrompt) return false;
    perm = await handle.requestPermission({ mode: "readwrite" });
    return perm === "granted";
  } catch { return false; }
}

async function writeFile(handle, blob, filename) {
  const fh = await handle.getFileHandle(filename, { create: true });
  const w = await fh.createWritable();
  await w.write(blob);
  await w.close();
}

export function supportsFolderSave() {
  return typeof window !== "undefined" && !!window.showDirectoryPicker;
}

export async function saveBlobToFolder(blob, filename) {
  if (!supportsFolderSave()) return false;
  try {
    if (!dirHandle) dirHandle = await idbGet();
    if (dirHandle && !(await ensurePermission(dirHandle, true))) dirHandle = null;
    if (!dirHandle) {
      dirHandle = await window.showDirectoryPicker({ mode: "readwrite" });
      idbSet(dirHandle);
    }
    await writeFile(dirHandle, blob, filename);
    return dirHandle.name || "selected folder";
  } catch (err) {
    if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) return null;
    // SecurityError: embedded/cross-origin frames may not show a picker — fall back to download
    if (err && err.name === "SecurityError") return false;
    throw err;
  }
}

export async function autoFilePDF(blob, filename) {
  if (!supportsFolderSave()) return null;
  try {
    if (!dirHandle) dirHandle = await idbGet();
    if (!dirHandle) return null;
    if (!(await ensurePermission(dirHandle, true))) return null;
    await writeFile(dirHandle, blob, filename);
    return dirHandle.name || "selected folder";
  } catch { return null; }
}

export function resetSaveFolder() {
  dirHandle = null;
}
